/**
 * Phone country codes for the player registration form.
 *
 * Sorted with platform's primary markets first, then alphabetical by name.
 * `flag` is the unicode regional indicator (2 letters) — renders as the
 * country emoji on any modern OS/browser without external sprite assets.
 */
export interface CountryCode {
  iso: string;          // ISO 3166-1 alpha-2
  dial: string;         // e.g. '+66'
  name: string;         // English display name
  flag: string;         // regional-indicator pair
  /**
   * How many digits a subscriber types for a LOCAL number, trunk prefix
   * included — 10 for Thailand (`0812345678`). Registration uses the phone
   * number as the username, so the count has to be exact per country rather
   * than a single hardcoded 10.
   *
   * Left undefined wherever the count is not settled (several of these markets
   * accept more than one length): those fall back to the generic E.164 shape
   * check, which accepts a real number and rejects a typo without inventing a
   * limit that would lock out a whole country. Fill one in as it is confirmed.
   */
  localDigits?: number;
}

export const COUNTRY_CODES: CountryCode[] = [
  // Primary markets — keep at top so the most-used codes are pre-visible.
  { iso: 'TH', dial: '+66',  name: 'Thailand',     flag: '🇹🇭', localDigits: 10 },
  { iso: 'VN', dial: '+84',  name: 'Vietnam',      flag: '🇻🇳' },
  { iso: 'CN', dial: '+86',  name: 'China',        flag: '🇨🇳' },
  { iso: 'KH', dial: '+855', name: 'Cambodia',     flag: '🇰🇭' },
  { iso: 'LA', dial: '+856', name: 'Laos',         flag: '🇱🇦' },
  { iso: 'MM', dial: '+95',  name: 'Myanmar',      flag: '🇲🇲' },
  { iso: 'MY', dial: '+60',  name: 'Malaysia',     flag: '🇲🇾' },
  { iso: 'ID', dial: '+62',  name: 'Indonesia',    flag: '🇮🇩' },
  { iso: 'PH', dial: '+63',  name: 'Philippines',  flag: '🇵🇭' },
  { iso: 'SG', dial: '+65',  name: 'Singapore',    flag: '🇸🇬' },
  // Rest, alphabetical
  { iso: 'AU', dial: '+61',  name: 'Australia',    flag: '🇦🇺' },
  { iso: 'BD', dial: '+880', name: 'Bangladesh',   flag: '🇧🇩' },
  { iso: 'BR', dial: '+55',  name: 'Brazil',       flag: '🇧🇷' },
  { iso: 'CA', dial: '+1',   name: 'Canada',       flag: '🇨🇦' },
  { iso: 'CH', dial: '+41',  name: 'Switzerland',  flag: '🇨🇭' },
  { iso: 'DE', dial: '+49',  name: 'Germany',      flag: '🇩🇪' },
  { iso: 'ES', dial: '+34',  name: 'Spain',        flag: '🇪🇸' },
  { iso: 'FR', dial: '+33',  name: 'France',       flag: '🇫🇷' },
  { iso: 'GB', dial: '+44',  name: 'United Kingdom', flag: '🇬🇧' },
  { iso: 'HK', dial: '+852', name: 'Hong Kong',    flag: '🇭🇰' },
  { iso: 'IN', dial: '+91',  name: 'India',        flag: '🇮🇳' },
  { iso: 'IT', dial: '+39',  name: 'Italy',        flag: '🇮🇹' },
  { iso: 'JP', dial: '+81',  name: 'Japan',        flag: '🇯🇵' },
  { iso: 'KR', dial: '+82',  name: 'South Korea',  flag: '🇰🇷' },
  { iso: 'MX', dial: '+52',  name: 'Mexico',       flag: '🇲🇽' },
  { iso: 'NL', dial: '+31',  name: 'Netherlands',  flag: '🇳🇱' },
  { iso: 'NZ', dial: '+64',  name: 'New Zealand',  flag: '🇳🇿' },
  { iso: 'PK', dial: '+92',  name: 'Pakistan',     flag: '🇵🇰' },
  { iso: 'RU', dial: '+7',   name: 'Russia',       flag: '🇷🇺' },
  { iso: 'TW', dial: '+886', name: 'Taiwan',       flag: '🇹🇼' },
  { iso: 'US', dial: '+1',   name: 'United States', flag: '🇺🇸' },
  { iso: 'ZA', dial: '+27',  name: 'South Africa', flag: '🇿🇦' },
];

export const DEFAULT_COUNTRY_ISO = 'TH';

export function findCountryByIso(iso: string): CountryCode | undefined {
  return COUNTRY_CODES.find((c) => c.iso === iso);
}
